import React from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Briefcase, Clock, MessageSquare, Mail, Star } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ConversationList = ({ 
  conversations, 
  selectedId, 
  onSelect, 
  loading,
  getChannelIcon 
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <MessageSquare className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <p className="text-muted-foreground">No conversations found</p>
        <p className="text-sm text-muted-foreground/70 mt-1">
          Conversations will appear here when customers message you
        </p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-full">
      <div className="divide-y">
        {conversations.map((conv) => {
          const isEmail = conv.type === 'email';
          
          return (
            <button
              key={conv.id}
              onClick={() => onSelect(conv)}
              className={`w-full p-3 text-left hover:bg-accent/50 transition-colors ${
                selectedId === conv.id ? 'bg-accent' : ''
              } ${conv.unread_count > 0 ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}
            >
              <div className="flex items-start gap-3">
                <Avatar className={`h-10 w-10 shrink-0 ${isEmail ? 'ring-2 ring-blue-200 dark:ring-blue-800' : ''}`}>
                  <AvatarFallback className={`text-sm ${isEmail ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : ''}`}>
                    {conv.customer_name?.split(' ').map(n => n[0]).join('').substring(0, 2) || 'U'}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`truncate ${conv.unread_count > 0 ? 'font-semibold' : 'font-medium'}`}>
                      {conv.customer_name || 'Unknown'}
                    </span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {conv.last_message_at && formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })}
                    </span>
                  </div>
                  
                  {/* Email subject line */}
                  {isEmail && conv.subject && (
                    <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                      {conv.subject}
                    </p>
                  )}
                  
                  {/* Job title for SMS */}
                  {!isEmail && conv.job_title && (
                    <div className="flex items-center gap-1 text-xs text-primary mt-0.5">
                      <Briefcase className="h-3 w-3" />
                      <span className="truncate">{conv.job_title}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mt-1">
                    {conv.last_message_channel && (
                      <span className="text-muted-foreground">
                        {getChannelIcon(conv.last_message_channel)}
                      </span>
                    )}
                    <p className="text-sm text-muted-foreground truncate flex-1">
                      {conv.last_message || 'No messages yet'}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2 mt-1.5">
                    {isEmail ? (
                      <Badge 
                        variant="outline" 
                        className="text-[10px] h-5 border-blue-500 text-blue-600 gap-1"
                      >
                        <Mail className="h-2.5 w-2.5" />
                        Email
                      </Badge>
                    ) : (
                      <Badge 
                        variant="outline" 
                        className={`text-[10px] h-5 ${
                          conv.bucket === 'lead_intake' ? 'border-blue-500 text-blue-600' :
                          conv.bucket === 'production' ? 'border-orange-500 text-orange-600' :
                          conv.bucket === 'service' ? 'border-purple-500 text-purple-600' :
                          conv.bucket === 'marketing' ? 'border-green-500 text-green-600' :
                          'border-gray-500 text-gray-600'
                        }`}
                      >
                        {conv.bucket || 'general'}
                      </Badge>
                    )}
                    
                    {conv.is_starred && (
                      <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                    )}
                    
                    {conv.status === 'waiting' && (
                      <Badge variant="outline" className="text-[10px] h-5 border-yellow-500 text-yellow-600">
                        <Clock className="h-3 w-3 mr-0.5" />
                        Waiting
                      </Badge>
                    )}
                    
                    {conv.unread_count > 0 && (
                      <Badge className={`text-white text-[10px] h-5 ml-auto ${isEmail ? 'bg-blue-500' : 'bg-red-500'}`}>
                        {conv.unread_count}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </ScrollArea>
  );
};

export default ConversationList;